import {
  Component,
  EventEmitter,
  Output,
  ChangeDetectorRef,
  OnDestroy,
} from '@angular/core';
import { AnimationService } from '../../element-editor/animation/animation.service';
import { ShapeService } from '../../element-editor/shape/shape.service';
import {
  StudioAnimationService,
  StudioService,
  selectVideoControls,
  VideoControlData,
  VideoControlSection,
} from '../../element-editor/store/studio';
import { Store } from '@ngrx/store';

@Component({
  selector: 'nw-video-controls',
  templateUrl: './video-controls.component.html',
  styleUrls: ['./video-controls.component.scss'],
})
export class VideoControlsComponent implements OnDestroy {
  videoControlsData: VideoControlData | null = null;
  hoveredSceneIndex: number | null = null;
  private animationFrameId: number | null = null;

  // Convenience getters for template access

  get currentSceneId() {
    return this.studioService.currentSceneId;
  }

  get totalDuration() {
    return this.videoControlsData?.totalDuration || 0;
  }

  get currentSceneIndex() {
    return this.studioService.currentSceneIndex;
  }

  get sceneSections() {
    return this.videoControlsData?.sceneSections || [];
  }

  get currentTime() {
    return this.animationService.currentAnimationTime;
    // return this.timeLine.time();
  }

  get duration() {
    return this.totalDuration;
  }

  get playing() {
    return this.studioService.animationIsPlaying;
  }

  @Output() playToggle = new EventEmitter<void>();
  @Output() seek = new EventEmitter<number>(); // 0..1 fraction

  get progressPct(): string | number {
    const percent =
      (this.studioService.currentAnimationTime / this.totalDuration) * 100;
    return `calc(${percent}% + 5px)`;
  }

  constructor(
    private readonly animationService: AnimationService,
    private readonly shapeService: ShapeService,
    private readonly studioService: StudioService,
    private readonly studioAnimationService: StudioAnimationService,
    private readonly store: Store,
    private readonly cdr: ChangeDetectorRef,
  ) {
    // Subscribe to video controls data from store
    this.store.select(selectVideoControls).subscribe(data => {
      this.videoControlsData = data;
    });

    // Start animation frame loop to update progress
    this.startProgressUpdate();
  }

  ngOnDestroy() {
    this.stopProgressUpdate();
  }

  /**
   * Start the animation frame loop to continuously update the progress
   */
  private startProgressUpdate() {
    const update = () => {
      // Trigger change detection to update the progress bar
      this.cdr.detectChanges();
      this.animationFrameId = requestAnimationFrame(update);
    };
    this.animationFrameId = requestAnimationFrame(update);
  }

  /**
   * Stop the animation frame loop
   */
  private stopProgressUpdate() {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  rewind() {
    this.studioService.resetAnimation();
    this.studioService.resetToStart();
  }

  forward() {
    this.studioService.goToLastScene();
  }

  play() {
    this.studioService.playPause();
  }

  pause() {
    this.studioService.playPause();
  }

  formatTime(totalSeconds: number): string {
    if (!isFinite(totalSeconds) || totalSeconds < 0) totalSeconds = 0;
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);
    const mm = hours > 0 ? String(minutes).padStart(2, '0') : String(minutes);
    const ss = String(seconds).padStart(2, '0');
    return hours > 0 ? `${hours}:${mm}:${ss}` : `${mm}:${ss}`;
  }

  onProgressClick(evt: MouseEvent) {
    const target = evt.currentTarget as HTMLElement | null;
    if (!target) return;
    const rect = target.getBoundingClientRect();
    const x = evt.clientX - rect.left;
    const fraction = Math.max(0, Math.min(1, x / rect.width));
    this.seek.emit(fraction);
  }

  /**
   * Get the width percentage for a scene section based on its duration
   */
  getSceneWidthPct(index: number): number {
    if (
      !this.totalDuration ||
      this.totalDuration <= 0 ||
      !this.sceneSections[index]
    )
      return 0;

    const section = this.sceneSections[index];
    const widthPct = (section.duration / this.totalDuration) * 100;

    return widthPct;
  }

  /**
   * Get the left offset percentage for a scene section
   */

  /**
   * Get the width in pixels for a scene line based on its duration
   */
  getSceneWidthPx(section: VideoControlSection): string {
    const { duration } = section;
    const widthPct = (duration / this.totalDuration) * 100;
    return `calc(${widthPct}% - 3px)`;
  }

  /**
   * Get the left offset for a scene in pixels
   */
  getSceneLeftPx(section: VideoControlSection): string {
    const { start } = section;
    return `${(start / this.totalDuration) * 100}%`;
  }

  /**
   * Handle scene line click to select that scene
   */
  onSceneClick(index: number) {
    this.studioService.goToScene(index);
  }

  /**
   * Handle mouse enter on scene line
   */
  onSceneMouseEnter(index: number) {
    this.hoveredSceneIndex = index;
  }

  /**
   * Handle mouse leave on scene line
   */
  onSceneMouseLeave() {
    this.hoveredSceneIndex = null;
  }

  /**
   * Check if a scene is currently selected
   */
  isSceneSelected(index: number): boolean {
    return this.sceneSections[index]?.sceneId === this.currentSceneId;
  }
}
